/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Import component UI form cho Sổ (S44-DN)
import RegisterOwnSharesPurchasedInfo from "./info";

import { RegisterOwnSharesPurchasedService } from "services/register-own-shares-purchased";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

// 1. Initial values khớp DTO CreateRegisterOwnSharesPurchasedDto
const initialValues = {
  unit: "",
  companyAddress: "",
  year: today.getFullYear().toString(),
  shareType: "",
  shareCode: "",
  measurementUnit: "",
  totalPages: "",
  openingDate: "",
  recorder: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signDate: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
  transactions: [],
};

function RegisterOwnSharesPurchasedDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [getData, setGetData] = useState({});
  const [data, setData] = useState({});

  const [formInitials, setFormInitials] = useState(initialValues);

  // 2. Load data từ API và map vào form
  useEffect(() => {
    (async () => {
      try {
        let apiData = {};
        if (id) {
          const response = await RegisterOwnSharesPurchasedService.getById(id);
          apiData = response.data ? response.data : response;
        } else {
          try {
            const response = await RegisterOwnSharesPurchasedService.getInit();
            apiData = response.data ? response.data : response;
          } catch (e) {
            console.log("No init endpoint", e);
          }
        }

        const formatDateVN = (isoString) => {
          if (!isoString) return "";
          const d = new Date(isoString);
          if (isNaN(d.getTime())) return isoString;
          return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
        };

        const mappedItems = (apiData.transactions || []).map((item) => ({
          recordingDate: formatDateVN(item.recordingDate),
          voucherNumber: item.voucherNumber || "",
          voucherDate: formatDateVN(item.voucherDate),
          description: item.description || "",
          correspondingAccount: item.correspondingAccount || "",
          actualPrice: item.actualPrice || "",
          increaseQuantity: item.increaseQuantity || "",
          increaseParValue: item.increaseParValue || "",
          increaseActualPurchasePrice: item.increaseActualPurchasePrice || "",
          decreaseQuantity: item.decreaseQuantity || "",
          decreaseParValue: item.decreaseParValue || "",
          decreaseActualPurchasePrice: item.decreaseActualPurchasePrice || "",
          balanceQuantity: item.balanceQuantity || "",
          balanceParValue: item.balanceParValue || "",
          balanceActualPurchasePrice: item.balanceActualPurchasePrice || "",
        }));

        // Parse signDate
        let sDay = initialValues.signingDay;
        let sMonth = initialValues.signingMonth;
        let sYear = initialValues.signingYear;
        if (apiData.signDate) {
          const parts = typeof apiData.signDate === "string" ? apiData.signDate.split("/") : [];
          if (parts.length === 3) {
            sDay = parts[0];
            sMonth = parts[1];
            sYear = parts[2];
          } else {
            const sDate = new Date(apiData.signDate);
            if (!isNaN(sDate.getTime())) {
              sDay = sDate.getDate().toString();
              sMonth = (sDate.getMonth() + 1).toString();
              sYear = sDate.getFullYear().toString();
            }
          }
        }

        const loadedData = {
          ...initialValues,
          companyAddress: apiData.companyAddress || apiData.address || apiData.company?.address || "",
          unit: apiData.company?.name || apiData.unit || "",
          year: apiData.year?.toString() || initialValues.year,
          shareType: apiData.shareType || "",
          shareCode: apiData.shareCode || "",
          measurementUnit: apiData.measurementUnit || "",
          totalPages: apiData.totalPages || "",
          openingDate: formatDateVN(apiData.openingDate),
          signingDay: sDay,
          signingMonth: sMonth,
          signingYear: sYear,
          recorder: apiData.recorder || "",
          chiefAccountant: apiData.chiefAccountant || "",
          legalRepresentative: apiData.legalRepresentative || "",
          transactions: mappedItems.length > 0 ? mappedItems : [],
        };

        setFormInitials(loadedData);
        setGetData(apiData);
      } catch (error) {
        console.error("Lỗi getById Sổ Cổ Phiếu Mua Lại:", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const onChangeValue = useCallback((type, value) => {
    setData((prev) => ({ ...prev, [type]: value }));
  }, []);

  const handleBack = () => navigation(-1);

  // 3. Submit payload 100% khớp CreateRegisterOwnSharesPurchasedDto
  const submitForm = async (values) => {
    try {
      const num = (val) => (val && !isNaN(val) ? Number(val) : 0);

      const getValue = (field) => (data[field] !== undefined ? data[field] : values[field]);
      const itemsFlat = getValue("transactions") || [];

      const parseDateVN = (dateStr) => {
        if (!dateStr) return "";
        const parts = dateStr.split("/");
        if (parts.length === 3) {
          return `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[2]}`;
        }
        const d = new Date(dateStr);
        if (!isNaN(d.getTime())) {
          return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
        }
        return dateStr;
      };

      // Build items theo Transaction schema
      const buildItems = (itemsList) =>
        itemsList.map((item) => ({
          recordingDate: parseDateVN(item.recordingDate),
          voucherNumber: item.voucherNumber || "",
          voucherDate: parseDateVN(item.voucherDate),
          description: item.description || "",
          correspondingAccount: item.correspondingAccount || "",
          actualPrice: num(item.actualPrice),
          increaseQuantity: num(item.increaseQuantity),
          increaseParValue: num(item.increaseParValue),
          increaseActualPurchasePrice: num(item.increaseActualPurchasePrice),
          decreaseQuantity: num(item.decreaseQuantity),
          decreaseParValue: num(item.decreaseParValue),
          decreaseActualPurchasePrice: num(item.decreaseActualPurchasePrice),
          balanceQuantity: num(item.balanceQuantity),
          balanceParValue: num(item.balanceParValue),
          balanceActualPurchasePrice: num(item.balanceActualPurchasePrice),
        }));

      // Build signDate ISO
      const signD = getValue("signingDay");
      const signM = getValue("signingMonth");
      const signY = getValue("signingYear");
      let signDate = null;
      if (signY && signM && signD) {
        signDate = `${String(signD).padStart(2, "0")}/${String(signM).padStart(2, "0")}/${signY}`;
      }

      // Payload chính xác theo DTO
      const payload = {
        unit: getValue("unit") || "",
        companyAddress: getValue("companyAddress") || "",
        year: getValue("year") ? getValue("year").toString() : new Date().getFullYear().toString(),
        shareType: getValue("shareType") || "",
        shareCode: getValue("shareCode") || "",
        measurementUnit: getValue("measurementUnit") || "",
        totalPages: num(getValue("totalPages")),
        openingDate: parseDateVN(getValue("openingDate")),
        signDate,
        recorder: getValue("recorder") || "",
        chiefAccountant: getValue("chiefAccountant") || "",
        legalRepresentative: getValue("legalRepresentative") || "",
        transactions: buildItems(itemsFlat),
      };

      console.log("✅ RegisterOwnSharesPurchased Payload:", JSON.stringify(payload, null, 2));

      if (!id) {
        await RegisterOwnSharesPurchasedService.create(payload);
        toast.success("Tạo Sổ Chi Tiết Cổ Phiếu thành công!");
      } else {
        await RegisterOwnSharesPurchasedService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
    } catch (error) {
      console.error("Lỗi lưu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
      throw error;
    }
  };

  const handlePrintDirect = (values) => {
    const finalData = { ...values, ...data };
    const htmlContent = getPrintTemplate({
      headerInfo: finalData,
      rows: finalData.transactions || [],
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const handleSubmit = async (values, actions) => {
    try {
      await submitForm(values);
    } catch (error) {
      // Error đã handle trong submitForm
    } finally {
      if (actions) {
        actions.setTouched({});
        actions.setSubmitting(false);
      }
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center" sx={{ height: "100%" }}>
          <Grid item xs={12} lg={12}>
            <Formik initialValues={formInitials} enableReinitialize={true} onSubmit={handleSubmit}>
              {({ values, errors, touched, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <SoftBox>
                        <RegisterOwnSharesPurchasedInfo
                          formData={{
                            values,
                            touched,
                            errors,
                            getData,
                            onChangeValue,
                            setFieldValue,
                          }}
                        />

                        <SoftBox mt={10} width="100%" display="flex" justifyContent="space-between">
                          <SoftButton variant="gradient" color="light" onClick={handleBack}>
                            Trở về
                          </SoftButton>

                          {containView ? (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                variant="gradient"
                                color="success"
                                onClick={() => handlePrintDirect(values)}
                                type="button"
                              >
                                In Sổ Chi Tiết (S44-DN)
                              </SoftButton>
                            </SoftBox>
                          ) : (
                            <SoftBox display="flex" gap={3}>
                              <SoftButton
                                disabled={isSubmitting}
                                type="submit"
                                variant="gradient"
                                color="dark"
                              >
                                {id ? "Lưu" : "Tạo"}
                              </SoftButton>
                            </SoftBox>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default RegisterOwnSharesPurchasedDetail;
